/**
 * GA4 Tracking Events – class-based module for dataLayer.push events.
 * Ensures dataLayer exists; use custom events from skin-assessment (or others) to fire only when validation/navigation succeeds.
 */
(function () {
  'use strict';

  if (typeof console !== 'undefined' && console.log) {
    console.log('[GA4 Tracking] script loaded');
  }
  window.dataLayer = window.dataLayer || [];

  var REDIRECT_STORAGE_KEY = 'ga4_redirect_event';

  function storeRedirectEvent(payload) {
    try {
      if (payload && typeof payload === 'object') {
        sessionStorage.setItem(REDIRECT_STORAGE_KEY, JSON.stringify(payload));
      }
    } catch (e) {
      // sessionStorage might be unavailable; fail silently
    }
  }

  function replayRedirectEventIfAny() {
    try {
      var stored = sessionStorage.getItem(REDIRECT_STORAGE_KEY);
      if (!stored) return;
      sessionStorage.removeItem(REDIRECT_STORAGE_KEY);
      var payload = JSON.parse(stored);
      if (payload && typeof payload === 'object') {
        pushEvent(payload);
      }
    } catch (e) {
      // Ignore malformed JSON or storage issues
    }
  }

  function isRedirectTarget(el) {
    return !!(el && el.getAttribute && el.getAttribute('data-ga4-redirect') === 'true');
  }

  /**
   * Pushes a single event object to dataLayer.
   * @param {Object} payload - Object to push (e.g. { event: 'sa_step1' }).
   */
  function pushEvent(payload) {
    console.log(payload,"pushEvent");
    if (payload && typeof payload === 'object') {
      window.dataLayer.push(payload);
    }
  }

  /**
   * GA4 Tracking handler: listens for custom events and pushes corresponding GA4 events.
   */
  var GA4Tracking = function GA4Tracking() {
    this.boundOnStep1Complete = this._onStep1Complete.bind(this);
    this.boundOnStep2Complete = this._onStep2Complete.bind(this);
    this.boundOnStartButtonClick = this._onStartButtonClick.bind(this);
    this.boundOnDocumentClick = this._onDocumentClick.bind(this);
    this.boundOnSlide5Visible = this._onSlide5Visible.bind(this);
  };

  GA4Tracking.prototype = {
    /**
     * Start listening for custom events (e.g. from skin-assessment) and button clicks.
     */
    init: function init() {
      // If previous page queued a redirect event, replay it now
      replayRedirectEventIfAny();

      document.addEventListener('skin-assessment-step1-complete', this.boundOnStep1Complete);
      document.addEventListener('skin-assessment-step2-complete', this.boundOnStep2Complete);
      document.addEventListener('skin-assessment-slide5-visible', this.boundOnSlide5Visible);
      document.addEventListener('click', this.boundOnStartButtonClick);
      document.addEventListener('click', this.boundOnDocumentClick);
    },

    /**
     * Stop listening (e.g. for SPA cleanup).
     */
    destroy: function destroy() {
      document.removeEventListener('skin-assessment-step1-complete', this.boundOnStep1Complete);
      document.removeEventListener('skin-assessment-step2-complete', this.boundOnStep2Complete);
      document.removeEventListener('skin-assessment-slide5-visible', this.boundOnSlide5Visible);
      document.removeEventListener('click', this.boundOnStartButtonClick);
      document.removeEventListener('click', this.boundOnDocumentClick);
    },

    /**
     * Fired when step 1 (contact form) is valid and user is moving to next slide.
     */
    _onStep1Complete: function _onStep1Complete() {
      pushEvent({ event: 'sa_step1' });
    },

    /**
     * Fired when step 2 (basic info) is valid and user is moving to next slide.
     */
    _onStep2Complete: function _onStep2Complete() {
      pushEvent({ event: 'sa_step2' });
    },

    /**
     * Fired when Start (skin analysis) button is clicked.
     */
    _onStartButtonClick: function _onStartButtonClick(e) {
      var target = e.target && (e.target.id === 'btn_start' || e.target.closest('#btn_start'));
      if (target) {
        pushEvent({ event: 'sa_skin_analysis_start' });
      }
    },

    /**
     * Document click: slide 4 Continue, bottom nav, center nav icon, lander CTA.
     */
    _onDocumentClick: function _onDocumentClick(e) {
      var target = e.target && e.target.closest && e.target.closest('[data-ga4-event]');
      if (target && target.getAttribute('data-ga4-event') === 'sa_skin_analysis_done') {
        var payloadContinue = { event: 'sa_skin_analysis_done' };
        if (isRedirectTarget(target)) {
          storeRedirectEvent(payloadContinue);
        } else {
          pushEvent(payloadContinue);
        }
        return;
      }
      target = e.target && e.target.closest && e.target.closest('[data-nav="skin-report"]');
      if (target) {
        var payloadSkinReport = { event: 'sa_skin_report_success' };
        if (isRedirectTarget(target)) {
          storeRedirectEvent(payloadSkinReport);
        } else {
          pushEvent(payloadSkinReport);
        }
        return;
      }
      target = e.target && e.target.closest && e.target.closest('[data-nav="consultation"]');
      if (target) {
        var payloadConsultation = { event: 'sa_derma_consultation' };
        if (isRedirectTarget(target)) {
          storeRedirectEvent(payloadConsultation);
        } else {
          pushEvent(payloadConsultation);
        }
        return;
      }
      target = e.target && e.target.closest && e.target.closest('[data-nav="products"]');
      if (target) {
        var payloadProducts = { event: 'sa_recommended_products' };
        if (isRedirectTarget(target)) {
          storeRedirectEvent(payloadProducts);
        } else {
          pushEvent(payloadProducts);
        }
        return;
      }
      target = e.target && e.target.closest && e.target.closest('.nav-item--center');
      if (target) {
        var payloadIcon = { event: 'sa_icon' };
        if (isRedirectTarget(target)) {
          storeRedirectEvent(payloadIcon);
        } else {
          pushEvent(payloadIcon);
        }
        return;
      }
      target = e.target && e.target.closest && e.target.closest('a.scan-btn-mobile');
      if (target) {
        var payloadLander = { event: 'sa_lander' };
        if (isRedirectTarget(target)) {
          storeRedirectEvent(payloadLander);
        } else {
          pushEvent(payloadLander);
        }
        return;
      }
      target = e.target && e.target.closest && e.target.closest('[data-action="confirm-consultation"]');
      if (target) {
        var payloadBookingSuccess = { event: 'sa_derma_booking_success' };
        if (isRedirectTarget(target)) {
          storeRedirectEvent(payloadBookingSuccess);
        } else {
          pushEvent(payloadBookingSuccess);
        }
        return;
      }
      target = e.target && e.target.closest && e.target.closest('[data-action="allow-location"]');
      if (target) {
        var payloadAllow = { event: 'sa_step3_allow' };
        if (isRedirectTarget(target)) {
          storeRedirectEvent(payloadAllow);
        } else {
          pushEvent(payloadAllow);
        }
        return;
      }
      target = e.target && e.target.closest && e.target.closest('[data-action="skip-location"]');
      if (target) {
        var payloadDeny = { event: 'sa_step3_deny' };
        if (isRedirectTarget(target)) {
          storeRedirectEvent(payloadDeny);
        } else {
          pushEvent(payloadDeny);
        }
        return;
      }
      target = e.target && e.target.closest && e.target.closest('.book-appointment-js');
      if (target) {
        var payloadBookNow = { event: 'sa_derma_book_now' };
        if (isRedirectTarget(target)) {
          storeRedirectEvent(payloadBookNow);
        } else {
          pushEvent(payloadBookNow);
        }
      }
    },

    /**
     * Fired when slide 5 (skin report) becomes visible.
     */
    _onSlide5Visible: function _onSlide5Visible() {
      pushEvent({ event: 'sa_skin_report_success' });
    }
  };

  /**
   * Factory: create and init a single instance.
   */
  function setup() {
    var instance = new GA4Tracking();
    instance.init();
    window.GA4TrackingInstance = instance;
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setup);
  } else {
    setup();
  }
})();
